/**
 * Player rig upgrade-in-place (E2.4.S2) — swaps a player entity's placeholder
 * capsule+nose for its skinned rig avatar asynchronously, keeping the entity's
 * `THREE.Group` as the stable anchor. Drives BOTH the local player (self) and
 * every remote player the client can see: appearance is a full-state field on
 * every player entity (AD-137), so one renderer serves both — self is driven from
 * `helloAck.you.appearance` in the frame loop, remotes from each `EntityState`'s
 * `appearance` on the entry/full-state path.
 *
 * This is the E2.3.S2 `CreatureRenderer` lifecycle applied to players (consumed as
 * a pattern, per the story's dependency contract — the shared `EntityGroup` type is
 * imported, the group-replace + monotonic-generation guard + orphan-drop reused,
 * never duplicated). It wraps the S1 `RigAvatarBuilder` (resolve → instantiate →
 * morph → wardrobe) with the group lifecycle:
 *
 *   upgrade(id, group, appearance):
 *     - build the avatar (or `null` for the graceful capsule fallback),
 *     - on settle REPLACE the placeholder children captured at upgrade time (the
 *       capsule body + nose cone) with the rig root, disposing their geometry/
 *       materials in place — the group's position / facing / health-bar / raycast
 *       are untouched (the group stays the anchor),
 *     - entity despawned / renderer disposed before the load settles ⇒ drop the
 *       clone, no orphan (renderer-wide monotonic generation + record-identity guard),
 *     - NO rig pack / unknown model / null template / load error ⇒ keep the capsule
 *       verbatim (the builder's own `null` path — the never-blank-avatar rule).
 *
 * The optional `onBuilt` callback fires after settle with the rig root (success) or
 * `null` (capsule fallback) so the caller can re-seat self-view equipment onto the
 * rig's bone sockets — `rigSocketPlacement` (below) builds the placement the E2.2
 * `EquipmentRenderer` consumes. Remotes carry no equipment (self-view only), so they
 * pass no callback.
 *
 * Nothing rides the per-tick path — upgrades key off snapshot entity-spawn / the
 * join-ack (AD-031), appearance is a full-state field never a per-tick delta
 * (AD-137), and the collider stays the body-invariant 0.4 m shared-sim circle
 * (appearance never reaches movement/collision). Node-safe like the builder: the
 * only `three` dependency is the `Object3D` TYPE (erased at runtime); the loader is
 * injected and the group is the abstract `EntityGroup`, so the whole lifecycle runs
 * under `node --test` with fakes (AD-120). Import boundary (AD-122): shared types
 * via `@great-cloud/shared/client`.
 */

import type { AnimationClip, Object3D } from 'three';

import type { AnimState, CharacterAppearance } from '@great-cloud/shared/client';

import { disposeObject3D, type GltfLoadFn } from '../assets/index.js';
import { RigAnimator, type RigMixerFactory } from './animator.js';
import { resolveAbilityClipFor, resolvePlayerActionClip } from './actionClips.js';
import type { RigAction } from './clips.js';
import type { EntityGroup } from '../creatures/index.js';
import type { AttachTarget, SocketPlacement, SocketPlacementResolver } from '../equipment/index.js';
import { RigAvatarBuilder, mergeClips, type BuiltRigAvatar } from './builder.js';
import { locomotionSpeedsFrom, type LocomotionSpeeds } from './locomotionRate.js';
import { RIG_BODY_MODEL, RIG_PACK_ID, type RigContent } from './resolve.js';
import {
  RIG_ROOT_SPACE_EQUIPMENT_SLOTS,
  rigBoneAttachOffset,
  rigLeftHandAttachOffset,
  rigMainhandAttachOffset,
  rigRootSpaceAttachOffset,
  resolveRigBone,
  resolveRigSockets,
  type BoneLookup,
} from './sockets.js';

const RIG_MULTIPART_EQUIPMENT_PARTS: Readonly<
  Record<string, readonly { nodeName: string; boneName: string }[]>
> = {
  hands: [
    { nodeName: 'equipment-part-right', boneName: 'RightForeArm' },
    { nodeName: 'equipment-part-left', boneName: 'LeftForeArm' },
  ],
  legs: [
    { nodeName: 'equipment-part-right', boneName: 'RightUpLeg' },
    { nodeName: 'equipment-part-left', boneName: 'LeftUpLeg' },
  ],
  feet: [
    { nodeName: 'equipment-part-right', boneName: 'RightFoot' },
    { nodeName: 'equipment-part-left', boneName: 'LeftFoot' },
  ],
};

/**
 * A local action result the own rig presents as a one-shot clip (AP5.S2): the `ok`
 * slice of the wire `gatherResult` / `craftResult` payloads, already delivered to
 * the store — nothing new rides the wire.
 */
export interface RigActionMoment {
  ok: boolean;
  /**
   * The gathering skill a `gatherResult` trained (present on success) — picks the
   * gather motion (`gatherKindForSkill`, actionClips.ts). Absent for crafts.
   */
  gatheringSkill?: string;
  /**
   * How long the action lasts, ms (AD-665: a gather CHANNEL's length) — the clip is played
   * at the rate that makes it end exactly then. Absent ⇒ the clip's own policy rate.
   */
  durationMs?: number;
}

/**
 * The latest local result + a change signal, built by App from a store slice
 * (`() => store.state.lastGather` + `store.subscribe`). A NEW result is detected
 * by object identity (the store replaces the slice per applied result), so a
 * stale value never re-triggers — the E2.2.S3 `GatherMomentSource` pattern.
 */
export interface RigActionMomentSource {
  get(): RigActionMoment | null;
  subscribe(listener: () => void): () => void;
}

/** Everything App hands Game so it can drive player rig avatars. */
export interface RigBridge {
  content: RigContent;
  /**
   * AP5.S2: the local gather/craft result signals that play the own rig's
   * `gather`/`craft` one-shots. Absent ⇒ those two actions never play (combat
   * actions still do — they come off the event channel, not the store).
   */
  moments?: {
    gather: RigActionMomentSource;
    craft: RigActionMomentSource;
  };
}

export interface PlayerRigRendererOptions {
  /** Presentation lookup (rig asset pack + appearance-option catalogue). */
  content: RigContent;
  /** Injected glTF loader (real: `createThreeGltfLoader()`; tests: a stub). */
  gltfLoad: GltfLoadFn;
  /** Static asset HTTP base for delivery URLs (`assetHttpBase()`); '' = same-origin. */
  httpBase?: string;
  /** GPU-resource disposer for a discarded mesh (default: three geometry/material dispose). */
  dispose?: (object: Object3D) => void;
  /** Non-fatal warning sink (default `console.warn`). */
  onWarn?: (message: string, error?: unknown) => void;
  /**
   * Injected animation-mixer construction (AP5.S1; real: `createThreeRigMixer()`).
   * Omit to disable animation entirely — rigs then render exactly as before, in
   * bind pose, which is what the node tests and any non-visual consumer want.
   */
  createMixer?: RigMixerFactory;
  /**
   * Build rigs WITHOUT the clips-only packs (the attack combos) until `releaseClipPacks()`
   * (q-mobile-first-frame-clip-split): the low tier's first frame then waits on the body
   * alone. A held rig attacks with its class clip (every combo step), never a lost swing.
   * It also builds on the lean body (`body-base-lean`): until release a held rig plays the
   * generic clips (`Left_Slash`, `Collect_Object`), the class/gather/craft clips arriving
   * with `body-actions`.
   */
  deferClipPacks?: boolean;
}

/** Fired after an upgrade settles: the rig root on success, `null` on capsule fallback. */
export type RigBuiltCallback = (root: Object3D | null) => void;

/** Per-entity upgrade record (one rig per entity; async generation-guarded). */
interface RigRecord {
  /** Renderer-wide monotonic id; a settled build applies only if the record still holds it. */
  generation: number;
  /** The entity's group (the stable anchor). */
  group: EntityGroup;
  /** The placeholder children captured at upgrade time (capsule body + nose) to replace. */
  placeholders: readonly Object3D[];
  /** The settled rig avatar, or null while building / on a graceful fallback. */
  avatar: BuiltRigAvatar | null;
  /** Built while the clip packs were held: their clips are still owed to this rig. */
  owesClipPacks: boolean;
}

function defaultWarn(message: string, error?: unknown): void {
  console.warn(`[player-rig] ${message}`, error ?? '');
}

/**
 * Drives player entities' rig avatars. Create it once against the content bridge,
 * `upgrade()` each player as it enters visibility (remotes) or as the own avatar
 * builds (self), `remove()` it when it leaves, and `dispose()` on scene/avatar
 * teardown. One shared `RigAvatarBuilder` (and thus one pack-loader/template cache)
 * serves every player — all players share the single `player-rig-v1` pack.
 */
export class PlayerRigRenderer {
  private readonly builder: RigAvatarBuilder;
  /** Presentation lookup — read for the body artifact's locomotion speeds at attach. */
  private readonly content: RigContent;
  private readonly disposeObject: (object: Object3D) => void;
  private readonly onWarn: (message: string, error?: unknown) => void;
  /** AP5.S1 clip playback; `null` when no mixer factory was injected (static rigs). */
  private readonly animator: RigAnimator | null;

  /** entityId → upgrade record. */
  private readonly records = new Map<number, RigRecord>();
  /**
   * entityId → the ORIGINAL placeholder children, memoised across a null-build
   * fallback (E2.4.S6): a re-triggered upgrade (Game.ts onContentPack) must never
   * capture late-attached children (e.g. the self-view equipment meshes) as
   * disposable placeholders. Cleared on success, remove and dispose.
   */
  private readonly placeholderMemo = new Map<number, readonly Object3D[]>();
  /** Renderer-wide monotonic attach id (see RigRecord.generation). */
  private nextGeneration = 0;
  private disposed = false;
  /** Clip packs held (`deferClipPacks`) until `releaseClipPacks()`. */
  private clipPacksHeld: boolean;
  /** The released packs' clips, fetched once for every owed rig. */
  private packClips: Promise<readonly AnimationClip[]> | null = null;
  /** Rigs whose pack clips have landed but which were mid-action: re-attached once idle. */
  private readonly packClipsPending = new Set<number>();
  /**
   * Entities whose last build settled on the capsule (no rig pack, a load error). Before the
   * content pack lands that is temporary (Game re-attempts on `onContentPack`); after it, it is
   * the final state, which the loading screen reports instead of waiting on
   * (q-asset-readiness-loading-screen). Cleared by a new attempt or removal.
   */
  private readonly fallbackIds = new Set<number>();

  /** Whether this entity's last rig build settled on the capsule (see `fallbackIds`). */
  isFallback(entityId: number): boolean {
    return this.fallbackIds.has(entityId);
  }

  /**
   * Capture-readiness counters for the dev scene dump (`window.__gcSceneDump`):
   * `playerRigsTracked` = entities with an upgrade record (in flight or settled),
   * `playerRigsSettled` = those whose rig has replaced the capsule. A capsule
   * FALLBACK (null build) drops its record, so it never blocks readiness — the
   * capsule is then the honest final state, not a still-loading one.
   */
  stats(): { playerRigsTracked: number; playerRigsSettled: number } {
    let settled = 0;
    for (const record of this.records.values()) if (record.avatar !== null) settled += 1;
    return { playerRigsTracked: this.records.size, playerRigsSettled: settled };
  }

  constructor(options: PlayerRigRendererOptions) {
    this.builder = new RigAvatarBuilder({
      content: options.content,
      gltfLoad: options.gltfLoad,
      httpBase: options.httpBase,
      dispose: options.dispose,
      onWarn: options.onWarn,
      // Held packs ⇒ the low tier: build on the lean body, its other clips arrive with the packs.
      leanBody: options.deferClipPacks ?? false,
    });
    this.content = options.content;
    this.disposeObject = options.dispose ?? disposeObject3D;
    this.onWarn = options.onWarn ?? defaultWarn;
    this.animator =
      options.createMixer === undefined
        ? null
        : new RigAnimator({ createMixer: options.createMixer, onWarn: options.onWarn });
    this.clipPacksHeld = options.deferClipPacks ?? false;
  }

  /**
   * Lift the clip-pack hold: fetch the packs once and merge their clips into every rig built
   * without them (re-attached at once, or when its current one-shot ends); later builds carry
   * them. Idempotent; a failed pack leaves the body's clips (warned).
   */
  releaseClipPacks(): void {
    if (!this.clipPacksHeld || this.disposed) return;
    this.clipPacksHeld = false;
    for (const [entityId, record] of this.records) {
      if (record.avatar !== null && record.owesClipPacks) this.completeClipPacks(entityId, record);
    }
  }

  /** Merge the released packs into one owed rig (a no-op if superseded or nothing new). */
  private completeClipPacks(entityId: number, record: RigRecord): void {
    record.owesClipPacks = false;
    this.packClips ??= this.builder.clipPackClips();
    void this.packClips
      .then((extra) => {
        const avatar = record.avatar;
        if (this.disposed || this.records.get(entityId) !== record || avatar === null) return;
        const clips = mergeClips(avatar.clips, extra);
        if (clips.length === avatar.clips.length) return;
        avatar.clips = clips;
        this.packClipsPending.add(entityId);
        this.attachPendingPackClips();
      })
      .catch((error: unknown) => {
        this.onWarn(`deferred clip packs (entity ${entityId}) failed; body clips stay`, error);
      });
  }

  /**
   * Re-attach each pending rig with its merged clips — but only while it shows no one-shot,
   * so a swing or a held `dead` is never cut by the late combos. The next frame's
   * `setAnimState` restores its locomotion state.
   */
  private attachPendingPackClips(): void {
    if (this.animator === null) {
      this.packClipsPending.clear();
      return;
    }
    for (const entityId of this.packClipsPending) {
      if (this.animator.activeAction(entityId) !== null) continue;
      this.packClipsPending.delete(entityId);
      const avatar = this.records.get(entityId)?.avatar;
      if (avatar == null) continue;
      this.animator.attach(entityId, avatar.root, avatar.clips, this.locomotionSpeeds());
    }
  }

  /**
   * Present an entity's wire animation state (AP5.S1). Safe and cheap to call
   * every frame — a repeated state is a no-op inside the animator. A no-op for
   * entities with no rig yet (capsule) or when animation is disabled.
   */
  setAnimState(entityId: number, state: AnimState): void {
    this.animator?.setState(entityId, state);
  }

  /**
   * Feed a player's rendered ground position for this frame (after `setAnimState`) so
   * the run plays at the rate that keeps its feet planted, and a slow start shows the
   * walk (`q-locomotion-speed-matched-playback`). No-op for a capsule / unknown id.
   */
  observeGround(entityId: number, x: number, z: number, dtMs: number): void {
    this.animator?.observeGround(entityId, x, z, dtMs);
  }

  /** The body artifact's authored ground speed per locomotion clip (asset-pack data). */
  private locomotionSpeeds(): LocomotionSpeeds {
    const entry = this.content
      .assetPack(RIG_PACK_ID)
      ?.assets?.find((asset) => asset.id === RIG_BODY_MODEL);
    return locomotionSpeedsFrom(entry?.locomotion);
  }

  /**
   * Play a one-shot action on an entity's rig, layered over its locomotion
   * (AP5.S2). Returns whether it started — `false` for an entity with no rig
   * (capsule, enemy, unknown id), a rig lacking a plausible clip, a held `dead`,
   * or when animation is disabled. Never throws.
   *
   * `variant` (2026-09-23) names WHAT the action is done with — a `WeaponClass` for
   * `attack`, a `GatherKind` for `gather`, a `CraftKind` for `craft`. When the rig
   * carries that variant's clip it plays under its derived policy
   * (`resolvePlayerActionClip`, actionClips.ts); otherwise — no variant, an unknown
   * one, or an older rig without the class clips — the generic path runs exactly as
   * before.
   */
  playAction(
    entityId: number,
    action: RigAction,
    variant?: string | null,
    /** AD-665: fit the whole clip to this many seconds (a gather channel); absent ⇒ its policy. */
    fitSeconds?: number,
    /** AD-670: the attack combo step (0-based; the SwingGate decides). */
    comboStep = 0,
  ): boolean {
    if (this.animator === null) return false;
    const clips = this.records.get(entityId)?.avatar?.clips;
    if (fitSeconds !== undefined && fitSeconds > 0 && clips !== undefined) {
      const resolved = resolvePlayerActionClip(
        action,
        variant,
        clips.map((clip) => clip.name),
      );
      const clip = clips.find((c) => c.name === resolved?.clip) as
        | { duration?: number }
        | undefined;
      if (resolved !== null && typeof clip?.duration === 'number' && clip.duration > 0) {
        const { holdSeconds: _hold, ...movementRule } = resolved.policy;
        void _hold; // holdSeconds is the gate's, not the mixer's
        return this.animator.playActionClip(entityId, action, resolved.clip, {
          ...movementRule,
          timeScale: clip.duration / fitSeconds,
        });
      }
    }
    if (variant != null && variant !== '' && clips !== undefined) {
      const resolved = resolvePlayerActionClip(
        action,
        variant,
        clips.map((clip) => clip.name),
        comboStep,
      );
      if (resolved !== null && resolved.source === 'class') {
        return this.animator.playActionClip(entityId, action, resolved.clip, resolved.policy);
      }
    }
    return this.animator.playAction(entityId, action);
  }

  /**
   * How long (wall seconds) `playAction(entityId, action, variant)` would own the pose:
   * the resolved clip's length — or its hold budget — over its rate. `null` without a rig
   * or a clip. The own swing's gate (swingGate.ts) uses it to chain, never restart.
   */
  actionWallSeconds(
    entityId: number,
    action: RigAction,
    variant?: string | null,
    comboStep = 0,
  ): number | null {
    const clips = this.records.get(entityId)?.avatar?.clips;
    if (clips === undefined) return null;
    const resolved = resolvePlayerActionClip(
      action,
      variant,
      clips.map((clip) => clip.name),
      comboStep,
    );
    if (resolved === null) return null;
    const clip = clips.find((c) => c.name === resolved.clip) as { duration?: number } | undefined;
    const length = typeof clip?.duration === 'number' ? clip.duration : null;
    if (length === null) return null;
    const hold = Math.min(length, resolved.policy.holdSeconds ?? Number.POSITIVE_INFINITY);
    return hold / (resolved.policy.timeScale ?? 1);
  }

  /**
   * Present a damaging ability (AD-670): its own clip for the weapon class when it has one
   * (`PLAYER_ABILITY_CLIPS`), else the class heavy clip for a `heavy` motion when the rig carries
   * it, else the class's step-1 attack, else the generic swing. Returns the wall seconds it owns
   * the pose, or `null` when nothing started.
   */
  playAbility(entityId: number, abilityId: string, variant: string | null): number | null {
    if (this.animator === null) return null;
    const clips = this.records.get(entityId)?.avatar?.clips;
    if (clips === undefined) return null;
    const resolved = resolveAbilityClipFor(
      abilityId,
      variant,
      clips.map((clip) => clip.name),
    );
    if (resolved === null) return null;
    const started =
      resolved.source === 'class'
        ? this.animator.playActionClip(entityId, 'attack', resolved.clip, resolved.policy)
        : this.animator.playAction(entityId, 'attack');
    if (!started) return null;
    const clip = clips.find((c) => c.name === resolved.clip) as { duration?: number } | undefined;
    const length = typeof clip?.duration === 'number' ? clip.duration : 0;
    const hold = Math.min(length, resolved.policy.holdSeconds ?? Number.POSITIVE_INFINITY);
    return hold / (resolved.policy.timeScale ?? 1);
  }

  /** The one-shot an entity's rig is presenting now, or `null` (the swing gate's `showing`). */
  activeAction(entityId: number): RigAction | null {
    return this.animator?.activeAction(entityId) ?? null;
  }

  /** End an entity's action explicitly (AP5.S2) — releases a held `dead` on respawn. */
  clearAction(entityId: number): void {
    this.animator?.clearAction(entityId);
  }

  /**
   * Advance every rig's animation. `dtMs` is the frame delta in MILLISECONDS; `isHeld` pauses
   * the rigs it names for a hit-stop beat (`RigAnimator.update`).
   */
  update(dtMs: number, isHeld?: (entityId: number) => boolean): void {
    this.animator?.update(dtMs, isHeld);
    if (this.packClipsPending.size > 0) this.attachPendingPackClips();
  }

  /**
   * Upgrade one player entity's placeholder to its rig avatar, asynchronously. A
   * `null` build (no rig pack delivered, body load error, null template, or a
   * dispose in flight) leaves the caller's capsule+nose in place (graceful). Calling
   * again for an entity that already has an in-flight/settled rig is a no-op; calling
   * again AFTER a fallback retries (E2.4.S6 — the onContentPack re-trigger), reusing
   * the memoised original placeholder set so late-attached children survive.
   *
   * `onBuilt` (optional, self only) fires after settle with the rig root (success)
   * or `null` (fallback) so the caller can re-seat self-view equipment.
   */
  upgrade(
    entityId: number,
    group: EntityGroup,
    appearance: CharacterAppearance,
    onBuilt?: RigBuiltCallback,
  ): void {
    if (this.disposed) return;
    if (this.records.has(entityId)) return; // already upgrading/upgraded this entity
    this.fallbackIds.delete(entityId);

    const generation = ++this.nextGeneration;
    // The capsule body + nose. On a retry after a fallback (E2.4.S6) reuse the
    // memoised original set — the group may since have gained non-placeholder
    // children (equipment attachments) that must survive the replacement.
    const placeholders = this.placeholderMemo.get(entityId) ?? [...group.children];
    this.placeholderMemo.set(entityId, placeholders);
    const record: RigRecord = {
      generation,
      group,
      placeholders,
      avatar: null,
      owesClipPacks: this.clipPacksHeld,
    };
    this.records.set(entityId, record);

    void this.builder
      .build(appearance, { skipClipPacks: record.owesClipPacks })
      .then((avatar) => {
        const current = this.records.get(entityId);
        if (current !== record || current.generation !== generation || this.disposed) {
          // Superseded (entity despawned / renderer gone) ⇒ dispose the clone, no orphan.
          if (avatar !== null) this.disposeAvatar(avatar);
          return;
        }
        if (avatar === null) {
          // No rig pack / load error / null template ⇒ keep the capsule (graceful).
          this.records.delete(entityId);
          this.fallbackIds.add(entityId);
          onBuilt?.(null);
          return;
        }
        // Replace the placeholder capsule+nose with the rig root IN PLACE (group anchor).
        for (const child of record.placeholders) {
          group.remove(child);
          this.disposeObject(child);
        }
        group.add(avatar.root);
        current.avatar = avatar;
        this.placeholderMemo.delete(entityId); // settled — no retry will need it
        // AP5.S1: start animating this rig. A rig with no usable clips is simply
        // not attached and stays in bind pose (the graceful, never-blank rule).
        // Speed-matched locomotion: the rig's measured authored ground speeds.
        this.animator?.attach(entityId, avatar.root, avatar.clips, this.locomotionSpeeds());
        // Released while this rig was building without the packs ⇒ owe them now.
        if (current.owesClipPacks && !this.clipPacksHeld) this.completeClipPacks(entityId, current);
        onBuilt?.(avatar.root);
      })
      .catch((error: unknown) => {
        // The builder degrades every failure to null; guard an unexpected throw so a
        // bad upgrade never breaks the frame loop (the capsule stays).
        this.records.delete(entityId);
        this.fallbackIds.add(entityId);
        this.onWarn(`rig upgrade (entity ${entityId}) failed`, error);
        onBuilt?.(null);
      });
  }

  /**
   * The player left — drop any settled rig and guard any in-flight build. The caller
   * removes the group from the scene separately; this frees the rig's GPU resources.
   * A no-op for an entity that was never upgraded (a capsule-only player).
   */
  remove(entityId: number): void {
    this.animator?.detach(entityId); // AP5.S1: no mixer outlives its avatar
    this.packClipsPending.delete(entityId);
    this.placeholderMemo.delete(entityId); // also for capsule-only (null-built) players
    this.fallbackIds.delete(entityId);
    const record = this.records.get(entityId);
    if (record === undefined) return;
    this.records.delete(entityId);
    if (record.avatar !== null) {
      record.group.remove(record.avatar.root);
      this.disposeAvatar(record.avatar);
    }
    // If still building (avatar null), the generation guard drops the pending clone.
  }

  /** Detach + dispose every rig and drop the builder's loader caches (teardown). */
  dispose(): void {
    this.disposed = true;
    for (const id of [...this.records.keys()]) this.remove(id);
    this.placeholderMemo.clear(); // incl. capsule-only entities with no record
    this.animator?.dispose(); // AP5.S1: stop every mixer
    this.builder.dispose();
  }

  /** Free a settled avatar's GPU resources — wardrobe is parented under the root. */
  private disposeAvatar(avatar: BuiltRigAvatar): void {
    this.disposeObject(avatar.root);
  }
}

/**
 * Build the equipment `SocketPlacementResolver` for a rendered rig (E2.4 socket
 * re-seat, convention d). Rigid held props attach to the resolved CHR-026 hand
 * bone, with a per-socket capsule fallback when that alias is absent. The CURRENT
 * rigid armour placeholders are already authored in full-avatar root space, so
 * they attach to `capsuleParent` at a zero transform — applying a bone or capsule
 * body-region transform would double their authored height. This is an honest
 * placeholder fit, not the skinned deformation required by EQP-004.
 *
 * Consumes S1's `resolveRigSockets` unchanged. `boneLookup` is expected to return
 * the real skeleton `THREE.Object3D` bones (Game passes `root.getObjectByName`); the
 * `RigBone` type only exposes `name`, so a bone parent is widened to the
 * `AttachTarget` the equipment renderer parents onto — a real Object3D (and a test
 * fake) both carry `add`/`remove`. Pure + node-safe (no `three`). The optional
 * `skinBind` (Game injects `bindSkinnedEquipment` over this rig's bones) adds the
 * skinned-armour path every slot tries first (`SkinnedAttach`).
 */
export function rigSocketPlacement(
  boneLookup: BoneLookup,
  capsuleParent: AttachTarget,
  skinBind?: (object: Object3D) => boolean,
): SocketPlacementResolver {
  const rigid = rigidRigSocketPlacement(boneLookup, capsuleParent);
  if (skinBind === undefined) return rigid;
  // q-player-armour-visual-fit: a skinned piece (EQP-004) binds to this rig's skeleton and
  // is parented to the avatar group; a rigid one keeps the placement below unchanged.
  return (slot: string, model: string, mesh?: string): SocketPlacement | undefined => {
    const place = rigid(slot, model, mesh);
    return place === undefined
      ? undefined
      : { ...place, skin: { parent: capsuleParent, bind: skinBind } };
  };
}

function rigidRigSocketPlacement(
  boneLookup: BoneLookup,
  capsuleParent: AttachTarget,
): SocketPlacementResolver {
  const resolved = resolveRigSockets(boneLookup);
  return (slot: string, model: string, mesh?: string): SocketPlacement | undefined => {
    if (RIG_ROOT_SPACE_EQUIPMENT_SLOTS.has(slot)) {
      return { parent: capsuleParent, transform: rigRootSpaceAttachOffset(model) };
    }
    const multipart = RIG_MULTIPART_EQUIPMENT_PARTS[slot];
    if (multipart !== undefined) {
      const parts = multipart.map((part) => ({
        part,
        bone: resolveRigBone(boneLookup, part.boneName),
      }));
      if (parts.some((part) => part.bone === null || part.bone === undefined)) {
        return undefined;
      }
      return {
        parts: parts.map(({ part, bone }) => ({
          nodeName: part.nodeName,
          parent: bone as unknown as AttachTarget,
          transform: rigBoneAttachOffset(model),
        })),
      };
    }
    // The bow is a left-lead/two-hand prop in EQP-002 even though the inventory
    // projection stores every weapon in `mainhand`. Seat its one rigid parent on
    // the left-hand socket; animation can move both arms without double-parenting
    // one mesh. A missing left hand preserves that socket's capsule fallback.
    const placementSlot = slot === 'mainhand' && model === 'bow-base' ? 'offhand' : slot;
    const resolution = resolved[placementSlot];
    if (resolution === undefined) return undefined; // no socket for this slot (e.g. trinket)
    if (resolution.mode === 'bone') {
      return {
        parent: resolution.bone as unknown as AttachTarget,
        // AD-838: a right-hand held prop closes the grip-r fist on its hilt (heldGrip.ts).
        // q-bow-left-hand-grip: the left-lead bow closes the grip-l fist on its riser.
        transform:
          placementSlot === 'mainhand'
            ? rigMainhandAttachOffset(model, mesh)
            : placementSlot !== slot
              ? rigLeftHandAttachOffset(model, mesh)
              : rigBoneAttachOffset(model),
      };
    }
    return { parent: capsuleParent, transform: resolution.transform };
  };
}
